'use client';

import { useWebMcpContext } from 'WebmcpSdk/provider';
import { getRegistry, getServerRegistry, subscribeRegistry } from 'WebmcpSdk/registry-bridge';
import type { WebMcpRegistry } from '@bomdisoft/webmcp-core';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * The registry a tool host should register against, whether or not the host sits
 * inside the provider's subtree.
 *
 * Context wins when it is available — a nested provider is the more specific
 * answer. Otherwise the host follows the published registry, so a host mounted
 * as a sibling of the provider, or before it, still attaches the moment one
 * exists rather than silently registering nothing.
 *
 * A host that already has context does not subscribe. Subscribing would re-render
 * it every time any provider mounts or unmounts, and callers like
 * `useUniversalTools` rebuild their tool list on each render — so an extra render
 * means an extra round of register calls. In-tree hosts therefore behave exactly
 * as they did before the bridge existed.
 */
export function useLiveRegistry(): WebMcpRegistry | null {
  const ctx = useWebMcpContext();
  const ctxRegistry = ctx?.registry ?? null;

  const subscribe = useCallback((onStoreChange: () => void) => (ctxRegistry ? () => undefined : subscribeRegistry(onStoreChange)), [ctxRegistry]);

  const published = useSyncExternalStore(subscribe, getRegistry, getServerRegistry);

  return ctxRegistry ?? published;
}
